import {toPascalCase} from "@util";
import {BaseEntity} from "../model/BaseEntity";
import {Pageable, Response} from "../model/Response";
import {EndpointBuilder} from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import {
  UseLazyQuery,
  UseMutation,
  UseQuery,
} from "@reduxjs/toolkit/dist/query/react/buildHooks";
import {MutationDefinition, QueryDefinition} from "@reduxjs/toolkit/query";
import {Any} from "../model/Any";
import {api} from ".";

export type UseFindEntity<T> = UseQuery<
  QueryDefinition<string | undefined, Any, Any, Pageable<T>>
>;
export type UseLazyFindEntity<T> = UseLazyQuery<
  QueryDefinition<string | undefined, Any, Any, Pageable<T>>
>;
export type UseFindEntityById<T> = UseQuery<
  QueryDefinition<string | number, Any, Any, T>
>;
export type UseLazyFindEntityById<T> = UseLazyQuery<
  QueryDefinition<string | number, Any, Any, T>
>;
export type UseFindAllEntity<T> = UseQuery<
  QueryDefinition<string | undefined, Any, Any, T[]>
>;
export type UseLazyFindAllEntity<T> = UseLazyQuery<
  QueryDefinition<string | undefined, Any, Any, T[]>
>;
export type UseAddEntity<T> = UseMutation<
  MutationDefinition<Partial<T>, Any, Any, Response<T>>
>;
export type UseUpdateEntity<T> = UseMutation<
  MutationDefinition<Partial<T>, Any, Any, Response<T>>
>;
export type UseDeleteEntity = UseMutation<
  MutationDefinition<(string | number)[], Any, Any, Response<unknown>>
>;
export type UseDeleteEntityById = UseMutation<
  MutationDefinition<string | number, Any, Any, Response<unknown>>
>;

export type CrudApi<T> = {
  useFindEntityById: UseFindEntityById<T>;
  useLazyFindEntityById: UseLazyFindEntityById<T>;
  useFindEntity: UseFindEntity<T>;
  useLazyFindEntity: UseLazyFindEntity<T>;
  useFindAllEntity: UseFindAllEntity<T>;
  useLazyFindAllEntity: UseLazyFindAllEntity<T>;
  useAddEntity: UseAddEntity<T>;
  useUpdateEntity: UseUpdateEntity<T>;
  useDeleteEntity: UseDeleteEntity;
  useDeleteEntityById: UseDeleteEntityById;
};

export const generateCrudApi = <T extends BaseEntity>(
  tag: string,
  route: string,
  headers?: Headers,
): CrudApi<T> => {
  const pascalCase = toPascalCase(tag);

  const enhancedApi = api.enhanceEndpoints({addTagTypes: [tag]});

  type builder = EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      object,
      FetchBaseQueryMeta
    >,
    string,
    "api"
  >;

  const findById = (builder: builder) =>
    builder.query<T, string>({
      query: id => ({url: `${route}/${id}`, headers: headers}),
      transformResponse: (response: Response<T>) => {
        return response.data;
      },
      providesTags: (_post, _err, id) => [{type: tag, id}],
    });

  const find = (builder: builder) =>
    builder.query<Pageable<T>, string | undefined>({
      query: params => ({
        url: params ? `${route}/search?${params}` : `${route}/list`,
        headers: headers,
      }),
      transformResponse: (response: Response<Pageable<T>>) => {
        return response.data;
      },
      providesTags: (
        result = {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: 0,
        },
      ) => [
        ...result.content.map(({id}) => ({type: tag, id}) as const),
        {type: tag, id: "LIST"},
      ],
    });

  const findAll = (builder: builder) =>
    builder.query<T[], void>({
      query: () => ({url: `${route}/all`, headers: headers}),
      transformResponse: (response: Response<T[]>) => {
        return response.data;
      },
      providesTags: (result = []) => [
        ...result.map(({id}) => ({type: tag, id}) as const),
        {type: tag, id: "ALL"},
      ],
    });

  const add = (builder: builder) =>
    builder.mutation<T, Partial<T>>({
      query: body => ({
        url: route,
        method: "POST",
        body,
        headers: headers,
      }),
      invalidatesTags: [
        {type: tag, id: "LIST"},
        {type: tag, id: "ALL"},
        {type: tag, id: "SEARCH"},
      ],
    });

  const update = (builder: builder) =>
    builder.mutation<T, Partial<T>>({
      query(data) {
        const {id, ...body} = data;
        return {
          url: `${route}/${id}`,
          method: "PUT",
          body,
          headers: headers,
        };
      },
      invalidatesTags: entity => [{type: tag, id: entity?.id}],
    });

  const remove = (builder: builder) =>
    builder.mutation<{success: boolean; ids: string}, string[]>({
      query(ids) {
        return {
          url: `${route}`,
          body: {ids: ids},
          method: "DELETE",
          headers: headers,
        };
      },
      invalidatesTags: () => [{type: tag, id: "LIST"}],
    });

  const deleteById = (builder: builder) =>
    builder.mutation<{success: boolean; id: string}, string[]>({
      query(id) {
        return {
          url: `${route}/${id}`,
          method: "DELETE",
          headers: headers,
        };
      },
      invalidatesTags: (_entity, error) =>
        error
          ? []
          : [
              {type: tag, id: "LIST"},
              {type: tag, id: "ALL"},
              {type: tag, id: "SEARCH"},
            ],
    });

  const crudApi = enhancedApi.injectEndpoints({
    endpoints: builder => ({
      [`find${pascalCase}ById`]: findById(builder),
      [`find${pascalCase}`]: find(builder),
      [`findAll${pascalCase}`]: findAll(builder),
      [`add${pascalCase}`]: add(builder),
      [`update${pascalCase}`]: update(builder),
      [`delete${pascalCase}`]: remove(builder),
      [`delete${pascalCase}ById`]: deleteById(builder),
    }),
    overrideExisting: true,
  });

  return {
    useLazyFindEntityById: crudApi[
      `useLazyFind${pascalCase}ByIdQuery` as keyof typeof crudApi
    ] as UseLazyFindEntityById<T>,
    useFindEntityById: crudApi[
      `useFind${pascalCase}ByIdQuery` as keyof typeof crudApi
    ] as UseFindEntityById<T>,
    useFindEntity: crudApi[
      `useFind${pascalCase}Query` as keyof typeof crudApi
    ] as UseFindEntity<T>,
    useLazyFindEntity: crudApi[
      `useLazyFind${pascalCase}Query` as keyof typeof crudApi
    ] as UseLazyFindEntity<T>,
    useLazyFindAllEntity: crudApi[
      `useLazyFindAll${pascalCase}Query` as keyof typeof crudApi
    ] as UseLazyFindAllEntity<T>,
    useFindAllEntity: crudApi[
      `useFindAll${pascalCase}Query` as keyof typeof crudApi
    ] as UseFindAllEntity<T>,
    useAddEntity: crudApi[
      `useAdd${pascalCase}Mutation` as keyof typeof crudApi
    ] as UseAddEntity<T>,
    useUpdateEntity: crudApi[
      `useUpdate${pascalCase}Mutation` as keyof typeof crudApi
    ] as UseUpdateEntity<T>,
    useDeleteEntity: crudApi[
      `useDelete${pascalCase}Mutation` as keyof typeof crudApi
    ] as UseDeleteEntity,
    useDeleteEntityById: crudApi[
      `useDelete${pascalCase}ByIdMutation` as keyof typeof crudApi
    ] as UseDeleteEntityById,
  };
};
